import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CalendarClock, CheckCircle2, Circle, Printer, Pencil, Trash2, Loader2,
  AlertTriangle, Landmark, Link2, HardHat,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { Mono, CAT_STYLES, STATUS_STYLES, fmtDate, Countdown, ProgramModal } from "./DrfaPrograms";

const token = () => localStorage.getItem("mdau_token");

export default function DrfaProgramDetail() {
  const { programId } = useParams();
  const [p, setP] = useState(null);
  const [claim, setClaim] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [toggling, setToggling] = useState("");
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/fieldclaim/programs/${programId}`);
      setP(data);
      if (data.claim_id) api.get(`/fieldclaim/claims/${data.claim_id}`).then((r) => setClaim(r.data)).catch(() => setClaim(null));
      else setClaim(null);
    } catch { nav("/dashboard/drfa"); }
  };
  useEffect(() => { load(); }, [programId]); // eslint-disable-line

  const toggle = async (key) => {
    setToggling(key);
    try { const { data } = await api.post(`/fieldclaim/programs/${programId}/milestones/${key}/toggle`); setP(data); }
    finally { setToggling(""); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this DRFA program? Deadline history will be removed.")) return;
    await api.delete(`/fieldclaim/programs/${programId}`);
    nav("/dashboard/drfa");
  };

  if (!p) return <div className="text-white/40 text-sm" data-testid="program-loading">Loading program…</div>;

  const sorted = [...p.milestones].sort((a, b) => a.due.localeCompare(b.due));

  return (
    <div className="space-y-8 text-white" data-testid="program-detail-page">
      <button onClick={() => nav("/dashboard/drfa")} data-testid="back-to-programs" className="inline-flex items-center gap-2 text-white/50 hover:text-gold-400 transition-colors text-sm">
        <ArrowLeft className="h-4 w-4" /> All programs
      </button>

      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="bg-orb-metallic text-navy-950 font-mono font-bold text-xs tracking-widest px-3 py-1.5 rounded-sm" data-testid="program-agrn-plate">
              {p.agrn || "NO AGRN"}
            </span>
            <Mono className="text-gold-400 text-[0.6rem]">NSWRA · DRFA deadline program</Mono>
          </div>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3" data-testid="program-title">{p.name}</h1>
          <div className="flex flex-wrap items-center gap-5 mt-3 text-white/55 font-light text-sm">
            <span className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-gold-500/60" /> Event {fmtDate(p.event_date)}</span>
            <span>Access {fmtDate(p.access_date)}</span>
            <span className="flex items-center gap-1.5"><HardHat className="h-3.5 w-3.5 text-gold-500/60" />{p.day_labour_opt_in ? "Day labour co-funded" : "No day-labour opt-in (21-day EW)"}</span>
            {claim && (
              <button onClick={() => nav(`/dashboard/fieldclaim/${claim.id}`)} data-testid="program-linked-claim" className="flex items-center gap-1.5 text-gold-300 hover:text-gold-200">
                <Link2 className="h-3.5 w-3.5" /> {claim.name}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${API}/fieldclaim/programs/${programId}/report?token=${token()}`} target="_blank" rel="noreferrer" data-testid="program-print-report" className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 hover:bg-gold-500/10 px-4 py-2.5 text-sm transition-colors">
            <Printer className="h-4 w-4" /> Event report
          </a>
          <button onClick={() => setEditOpen(true)} data-testid="program-edit-btn" className="border border-gold-500/25 text-white/60 hover:text-gold-300 p-2.5 transition-colors"><Pencil className="h-4 w-4" /></button>
          <button onClick={remove} data-testid="program-delete-btn" className="border border-red-500/25 text-red-300/70 hover:text-red-300 p-2.5 transition-colors"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      {/* stat plates */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="program-stats">
        <Stat label="Days since event" value={`${p.days_since_event}d`} />
        <Stat label="Milestones complete" value={`${p.progress.done}/${p.progress.total}`} accent={p.progress.pct === 100 ? "text-emerald-300" : "text-gold-300"} />
        <Stat label="Overdue" value={p.overdue_count} accent={p.overdue_count > 0 ? "text-red-300" : "text-emerald-300"} />
        <Stat label="Next deadline" value={p.next_due ? `${p.next_due.days_left}d` : "—"}
          sub={p.next_due ? p.next_due.title : "All complete"} accent="text-amber-300" />
      </div>

      {/* program horizon bar */}
      <div className="border border-gold-500/25 bg-navy-900/50 p-6" data-testid="program-horizon">
        <div className="flex items-center justify-between mb-5">
          <Mono className="text-[0.55rem] text-white/50">Program horizon — event → allowable time limit</Mono>
          <Mono className="text-[0.55rem] text-gold-300">ATL {fmtDate(p.atl)}</Mono>
        </div>
        <div className="relative h-2 bg-navy-800 border border-gold-500/15 rounded-full mt-4 mb-9">
          <div className="absolute inset-y-0 left-0 bg-orb-metallic rounded-full" style={{ width: `${p.elapsed_pct}%` }} />
          {/* milestone ticks */}
          {sorted.map((m) => {
            const span = new Date(p.atl) - new Date(p.event_date);
            const pos = Math.max(0, Math.min(100, ((new Date(m.due) - new Date(p.event_date)) / span) * 100));
            const c = m.done ? "bg-emerald-400" : m.status === "overdue" ? "bg-red-400" : CAT_STYLES[m.category].dot;
            return <div key={m.key} title={`${m.title} — ${m.due}`} className={`absolute -top-1 h-4 w-1 rounded-full ${c}`} style={{ left: `${pos}%` }} />;
          })}
          {/* today marker */}
          <div className="absolute -top-3 flex flex-col items-center" style={{ left: `${p.elapsed_pct}%` }} data-testid="program-today-marker">
            <div className="h-8 w-0.5 bg-white/80" />
            <span className="font-mono text-[0.5rem] uppercase tracking-widest text-white/70 mt-1">Today</span>
          </div>
        </div>
        <div className="flex justify-between font-mono text-[0.55rem] uppercase tracking-widest text-white/40">
          <span>Event · {fmtDate(p.event_date)}</span>
          <span>FY end · {fmtDate(p.fy_end)}</span>
          <span>ATL · {fmtDate(p.atl)}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* timeline */}
        <div className="lg:col-span-2 border border-gold-500/25 bg-navy-900/40 p-6" data-testid="program-timeline">
          <Mono className="text-[0.55rem] text-white/50">Deadline program — pushed out from the event date</Mono>
          <div className="relative mt-6 pl-8">
            <div className="absolute left-[11px] top-1 bottom-1 w-px bg-gradient-to-b from-gold-500/60 via-gold-500/25 to-gold-500/5" />
            <div className="space-y-5">
              {sorted.map((m) => {
                const cat = CAT_STYLES[m.category];
                const st = STATUS_STYLES[m.status];
                return (
                  <div key={m.key} className="relative" data-testid={`milestone-${m.key}`}>
                    <button onClick={() => toggle(m.key)} disabled={toggling === m.key} data-testid={`milestone-toggle-${m.key}`}
                      title={m.done ? "Mark as open" : "Mark complete"}
                      className={`absolute -left-8 top-0.5 h-6 w-6 rounded-full border flex items-center justify-center transition-colors bg-navy-950
                        ${m.done ? "border-emerald-400 text-emerald-400" : m.status === "overdue" ? "border-red-400 text-red-400 animate-pulse" : m.status === "due_soon" ? "border-amber-400 text-amber-400" : "border-gold-500/40 text-white/30 hover:text-gold-300"}`}>
                      {toggling === m.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : m.done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-3 w-3" />}
                    </button>
                    <div className={`border p-4 transition-colors ${m.done ? "border-emerald-400/25 bg-emerald-400/5" : m.status === "overdue" ? "border-red-500/30 bg-red-500/5" : "border-gold-500/20 bg-navy-950/50"}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className={`font-heading text-base leading-snug ${m.done ? "line-through text-white/45" : ""}`}>{m.title}</div>
                          <p className="text-white/45 text-xs font-light mt-1 max-w-xl">{m.description}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className="font-mono text-sm text-gold-300">{fmtDate(m.due)}</span>
                          <Countdown days={m.days_left} done={m.done} />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <span className={`border text-[0.55rem] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${cat.chip}`}>{cat.label}</span>
                        <span className={`border text-[0.55rem] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${st.cls}`} data-testid={`milestone-status-${m.key}`}>{st.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* claim cycles + linked claim */}
        <div className="space-y-6">
          <div className="border border-gold-500/25 bg-navy-900/40 p-6" data-testid="program-claim-cycles">
            <Mono className="text-[0.55rem] text-white/50">Biannual claim lodgement (CMS portal)</Mono>
            <div className="space-y-2.5 mt-4">
              {p.claim_cycles.map((c) => (
                <div key={c.due} className={`flex items-center justify-between border px-3 py-2.5 text-xs ${c.past ? "border-white/10 text-white/30" : "border-emerald-400/25 bg-emerald-400/5 text-white/75"}`}>
                  <span className="font-light pr-2">{c.label}</span>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-gold-300/90">{fmtDate(c.due)}</div>
                    <div className={`font-mono text-[0.6rem] ${c.past ? "text-white/25" : "text-emerald-300"}`}>{c.past ? "passed" : `${c.days_left}d left`}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {claim && (
            <div className="border border-gold-500/25 bg-navy-900/40 p-6" data-testid="program-claim-summary">
              <Mono className="text-[0.55rem] text-white/50">Linked event evidence</Mono>
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm"><Landmark className="h-4 w-4 text-gold-500/60" /> {claim.council || claim.name}</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[["Assets", claim.asset_count], ["Photos", claim.photo_count], ["Ready", `${claim.readiness_pct || 0}%`]].map(([k, v]) => (
                    <div key={k} className="border border-gold-500/15 bg-navy-950 py-2.5">
                      <div className="font-mono text-lg text-gold-300">{v}</div>
                      <Mono className="text-[0.5rem] text-white/40">{k}</Mono>
                    </div>
                  ))}
                </div>
                {p.overdue_count > 0 && (
                  <div className="flex items-start gap-2 text-amber-300/90 text-xs border border-amber-500/25 bg-amber-500/5 px-3 py-2.5">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {p.overdue_count} deadline(s) overdue — review before the next lodgement cycle.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {editOpen && <ProgramModal program={p} onClose={() => setEditOpen(false)} onSaved={(np) => { setEditOpen(false); setP(np); }} />}
    </div>
  );
}

function Stat({ label, value, sub, accent = "text-gold-300" }) {
  return (
    <div className="border border-gold-500/25 bg-navy-900/50 px-5 py-4">
      <Mono className="text-[0.5rem] text-white/40">{label}</Mono>
      <div className={`font-mono text-2xl mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-white/45 text-[0.65rem] font-light mt-0.5 truncate">{sub}</div>}
    </div>
  );
}
